import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, ChevronDown, ChevronRight, FileText, ExternalLink, Check, Receipt } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatDate(d) {
  if (!d) return '-';
  const head = String(d).slice(0, 10).replace(/\//g, '-');
  const parts = head.split('-');
  return parts.length === 3 && parts.every(Boolean) ? `${parts[2]}-${parts[1]}-${parts[0]}` : String(d);
}

export default function FatturazionePrevistaPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ monthly: [], totale_complessivo: 0 });
  const [openMonths, setOpenMonths] = useState({});
  const [openFornitori, setOpenFornitori] = useState({});
  const [loading, setLoading] = useState(true);
  // iter53: dialog per scegliere se creare la fattura o solo marcare
  const [actionOpen, setActionOpen] = useState(false);
  const [actionTarget, setActionTarget] = useState(null);  // fornitore object
  const [creaForm, setCreaForm] = useState({ numero_fornitore: '', data: '', scadenza: '', aliquota_iva: 22, descrizione_override: '' });
  const [creaMode, setCreaMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      const r = await axios.get(`${API}/ddt/fatturazione-prevista`, { withCredentials: true });
      const payload = r.data || { monthly: [], totale_complessivo: 0 };
      setData(payload);
      // Apri di default tutti i mesi
      const initialOpen = {};
      (payload.monthly || []).forEach(m => { initialOpen[m.month] = true; });
      setOpenMonths(initialOpen);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); }, []);

  const toggleMonth = (m) => setOpenMonths(p => ({ ...p, [m]: !p[m] }));
  const toggleFornitore = (k) => setOpenFornitori(p => ({ ...p, [k]: !p[k] }));

  const openFornitoreByPiva = (piva, ragSociale) => {
    if (piva) navigate(`/admin/fornitori?piva=${encodeURIComponent(piva)}`);
    else if (ragSociale) navigate(`/admin/fornitori?q=${encodeURIComponent(ragSociale)}`);
  };

  const handleMarkFornitoreFatturato = (forn) => {
    setActionTarget(forn);
    const today = new Date().toISOString().slice(0, 10);
    const primoDDT = forn.ddt?.[0] || {};
    setCreaForm({
      numero_fornitore: '',
      data: today,
      scadenza: primoDDT.data_pagamento_prevista || '',
      aliquota_iva: 22,
      descrizione_override: '',
    });
    setCreaMode(false);
    setActionOpen(true);
  };

  const doMarkOnly = async () => {
    if (!actionTarget) return;
    setSubmitting(true);
    try {
      await Promise.all(actionTarget.ddt.map(d => axios.post(`${API}/ddt/${d.id}/mark-fatturato`, {}, { withCredentials: true })));
      toast.success(`${actionTarget.n_ddt} DDT marcati come fatturati`);
      setActionOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    } finally {
      setSubmitting(false);
    }
  };

  const doCreaFattura = async () => {
    if (!actionTarget) return;
    if (!creaForm.numero_fornitore.trim()) {
      toast.error('Inserisci il numero della fattura del fornitore');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/ddt/crea-fattura-da-ddt`, {
        ddt_ids: actionTarget.ddt.map(d => d.id),
        numero_fornitore: creaForm.numero_fornitore.trim(),
        data: creaForm.data || null,
        scadenza: creaForm.scadenza || null,
        aliquota_iva: parseFloat(creaForm.aliquota_iva) || 22,
        descrizione_override: creaForm.descrizione_override || '',
      }, { withCredentials: true });
      toast.success(res.data?.message || 'Fattura creata');
      setActionOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore creazione fattura');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><CalendarClock className="h-7 w-7 text-amber-600" />Fatturazione prevista</h1>
          <p className="text-slate-500 mt-1">DDT raggruppati per mese e fornitore, in attesa di essere fatturati</p>
        </div>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase text-right">Totale complessivo atteso</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 mt-1">
              <p className="text-xs text-slate-500 text-right">Netto</p>
              <p className="text-2xl font-bold text-amber-700 text-right">€ {(data.totale_complessivo || 0).toFixed(2)}</p>
              <p className="text-xs text-slate-500 text-right">Lordo (IVA inc.)</p>
              <p className="text-2xl font-bold text-amber-900 text-right">€ {(data.totale_complessivo_lordo || 0).toFixed(2)}</p>
            </div>
            <p className="text-xs text-slate-500 mt-1 text-right">{data.monthly?.length || 0} mes{(data.monthly?.length || 0) === 1 ? 'e' : 'i'}</p>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <Card><CardContent className="p-10 text-center text-slate-400">Caricamento…</CardContent></Card>
      ) : (data.monthly || []).length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-slate-500">
            <FileText className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="font-medium">Nessun DDT in attesa di fatturazione</p>
            <p className="text-sm text-slate-400 mt-1">Imposta una "Data fatturazione prevista" sui DDT per vederli qui</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {(data.monthly || []).map(month => {
            const isMonthOpen = openMonths[month.month] !== false;
            return (
              <Card key={month.month} className="border-slate-200" data-testid={`month-${month.month}`}>
                <button
                  type="button"
                  onClick={() => toggleMonth(month.month)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition"
                  data-testid={`toggle-month-${month.month}`}
                >
                  <div className="flex items-center gap-3">
                    {isMonthOpen ? <ChevronDown className="h-5 w-5 text-slate-400" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
                    <div className="text-left">
                      <h2 className="text-lg font-bold capitalize">{month.month_label || month.month}</h2>
                      <p className="text-xs text-slate-500">{month.n_ddt} DDT · {month.fornitori.length} forniton{month.fornitori.length === 1 ? 're' : 'ri'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-slate-500 uppercase">Netto · Lordo</p>
                    <p className="text-xl font-bold text-amber-700">€ {month.totale_atteso.toFixed(2)} <span className="text-slate-400 mx-1">·</span> <span className="text-amber-900">€ {(month.totale_atteso_lordo || 0).toFixed(2)}</span></p>
                  </div>
                </button>
                {isMonthOpen && (
                  <CardContent className="p-0 border-t border-slate-200">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-amber-50/40">
                          <TableHead className="w-6"></TableHead>
                          <TableHead className="text-xs uppercase">Fornitore</TableHead>
                          <TableHead className="text-xs uppercase">P.IVA</TableHead>
                          <TableHead className="text-xs uppercase text-center">N° DDT</TableHead>
                          <TableHead className="text-xs uppercase text-right">Netto</TableHead>
                          <TableHead className="text-xs uppercase text-right">Lordo (IVA inc.)</TableHead>
                          <TableHead className="text-xs uppercase text-right">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {month.fornitori.map(forn => {
                          const fKey = `${month.month}__${forn.fornitore}`;
                          const isOpen = openFornitori[fKey];
                          return (
                            <>
                              <TableRow key={fKey} className="border-slate-100" data-testid={`forn-${fKey}`}>
                                <TableCell>
                                  <button type="button" onClick={() => toggleFornitore(fKey)} className="p-0.5 hover:bg-slate-100 rounded" data-testid={`toggle-forn-${fKey}`}>
                                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                  </button>
                                </TableCell>
                                <TableCell className="font-bold">{forn.fornitore}</TableCell>
                                <TableCell>
                                  {forn.fornitore_piva ? (
                                    <button
                                      type="button"
                                      onClick={() => openFornitoreByPiva(forn.fornitore_piva, forn.fornitore)}
                                      className="text-blue-600 hover:text-blue-800 underline font-mono text-xs inline-flex items-center gap-1"
                                      data-testid={`piva-link-${forn.fornitore_piva}`}
                                      title="Apri scheda fornitore"
                                    >
                                      {forn.fornitore_piva}
                                      <ExternalLink className="h-3 w-3" />
                                    </button>
                                  ) : (
                                    <Badge variant="outline" className="text-[10px]">P.IVA non assegnata</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-center"><Badge variant="outline">{forn.n_ddt}</Badge></TableCell>
                                <TableCell className="text-right font-bold text-amber-700">€ {forn.totale_atteso.toFixed(2)}</TableCell>
                                <TableCell className="text-right font-bold text-amber-900">€ {(forn.totale_atteso_lordo || 0).toFixed(2)}</TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-xs h-7 border-blue-300 text-blue-700 hover:bg-blue-50"
                                    onClick={() => handleMarkFornitoreFatturato(forn)}
                                    data-testid={`mark-fatt-${fKey}`}
                                  >
                                    <Receipt className="h-3 w-3 mr-1" />Fatturazione…
                                  </Button>
                                </TableCell>
                              </TableRow>
                              {isOpen && (
                                <TableRow className="bg-slate-50">
                                  <TableCell colSpan={7} className="p-0">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead className="text-[10px] uppercase">Numero DDT</TableHead>
                                          <TableHead className="text-[10px] uppercase">Data DDT</TableHead>
                                          <TableHead className="text-[10px] uppercase">Data fatt. prevista</TableHead>
                                          <TableHead className="text-[10px] uppercase">Data pag. prevista</TableHead>
                                          <TableHead className="text-[10px] uppercase">Descrizione</TableHead>
                                          <TableHead className="text-[10px] uppercase text-right">Netto</TableHead>
                                          <TableHead className="text-[10px] uppercase text-right">Lordo</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {forn.ddt.map(d => (
                                          <TableRow key={d.id}>
                                            <TableCell className="font-mono text-xs">{d.numero}</TableCell>
                                            <TableCell className="text-xs">{formatDate(d.data)}</TableCell>
                                            <TableCell className="text-xs font-medium">{formatDate(d.data_fatturazione_prevista)}</TableCell>
                                            <TableCell className="text-xs text-slate-500">{formatDate(d.data_pagamento_prevista)}</TableCell>
                                            <TableCell className="text-xs text-slate-700">{(d.descrizione || '').slice(0, 80)}{(d.descrizione || '').length > 80 ? '…' : ''}</TableCell>
                                            <TableCell className="text-right text-xs font-medium">€ {(d.costo_netto || 0).toFixed(2)}</TableCell>
                                            <TableCell className="text-right text-xs font-medium text-slate-900">€ {(d.costo_lordo || 0).toFixed(2)}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* iter53: Dialog scelta azione (marca only vs crea fattura ricevuta) */}
      <Dialog open={actionOpen} onOpenChange={setActionOpen}>
        <DialogContent className="max-w-xl" data-testid="action-fatturazione-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-blue-600" />
              {creaMode ? 'Crea Fattura Ricevuta' : 'Fatturazione DDT'}
            </DialogTitle>
          </DialogHeader>
          {actionTarget && !creaMode && (
            <div className="space-y-4">
              <div className="bg-slate-50 border rounded-md p-3 text-sm">
                <p><strong>Fornitore:</strong> {actionTarget.fornitore}</p>
                <p><strong>DDT da fatturare:</strong> {actionTarget.n_ddt}</p>
                <p><strong>Imponibile:</strong> € {actionTarget.totale_atteso.toFixed(2)}</p>
                <p><strong>Lordo IVA inc.:</strong> € {(actionTarget.totale_atteso_lordo || 0).toFixed(2)}</p>
              </div>
              <p className="text-sm text-slate-700">Cosa vuoi fare con questi DDT?</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setCreaMode(true)}
                  className="border-2 border-blue-300 bg-blue-50 hover:bg-blue-100 rounded-lg p-4 text-left transition-colors"
                  data-testid="opt-crea-fattura"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Receipt className="h-5 w-5 text-blue-600" />
                    <span className="font-semibold text-blue-900">Crea Fattura Ricevuta</span>
                  </div>
                  <p className="text-xs text-blue-700">Genera una nuova FAT-P pre-popolata (fornitore, imponibile, IVA, scadenza) e collega i DDT alla fattura. Consigliato.</p>
                </button>
                <button
                  type="button"
                  onClick={doMarkOnly}
                  disabled={submitting}
                  className="border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 rounded-lg p-4 text-left transition-colors disabled:opacity-50"
                  data-testid="opt-mark-only"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="h-5 w-5 text-amber-600" />
                    <span className="font-semibold text-amber-900">Solo marca come fatturati</span>
                  </div>
                  <p className="text-xs text-amber-700">Marca i DDT come "fatturato" senza creare la fattura nel gestionale. Utile se la fattura viene caricata manualmente.</p>
                </button>
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setActionOpen(false)}>Annulla</Button>
              </div>
            </div>
          )}
          {actionTarget && creaMode && (
            <form onSubmit={(e) => { e.preventDefault(); doCreaFattura(); }} className="space-y-3">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs space-y-1">
                <p><strong>Fornitore:</strong> {actionTarget.fornitore}</p>
                <p><strong>{actionTarget.n_ddt} DDT</strong> · Imponibile € {actionTarget.totale_atteso.toFixed(2)}</p>
              </div>
              <div className="space-y-2">
                <Label>Numero fattura fornitore *</Label>
                <Input
                  value={creaForm.numero_fornitore}
                  onChange={(e) => setCreaForm({ ...creaForm, numero_fornitore: e.target.value })}
                  placeholder="es. FT/2026/123"
                  required
                  data-testid="numero-fornitore-input"
                />
                <p className="text-[10px] text-slate-500">Il numero stampato sulla fattura del fornitore</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Data fattura</Label>
                  <Input
                    type="date"
                    value={creaForm.data}
                    onChange={(e) => setCreaForm({ ...creaForm, data: e.target.value })}
                    data-testid="data-fattura-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Scadenza pagamento</Label>
                  <Input
                    type="date"
                    value={creaForm.scadenza}
                    onChange={(e) => setCreaForm({ ...creaForm, scadenza: e.target.value })}
                    data-testid="scadenza-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Aliquota IVA %</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={creaForm.aliquota_iva}
                  onChange={(e) => setCreaForm({ ...creaForm, aliquota_iva: e.target.value })}
                  className="w-32"
                  data-testid="aliquota-iva-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Descrizione (opzionale)</Label>
                <Input
                  value={creaForm.descrizione_override}
                  onChange={(e) => setCreaForm({ ...creaForm, descrizione_override: e.target.value })}
                  placeholder={`Fatturazione DDT: ${actionTarget.ddt.map(d => d.numero).join(', ').slice(0, 60)}`}
                  data-testid="descrizione-input"
                />
              </div>
              <div className="bg-slate-50 border rounded-md p-3 text-sm">
                <div className="flex justify-between"><span>Imponibile:</span><strong>€ {actionTarget.totale_atteso.toFixed(2)}</strong></div>
                <div className="flex justify-between text-slate-600 text-xs"><span>IVA {creaForm.aliquota_iva}%:</span><span>€ {(actionTarget.totale_atteso * (parseFloat(creaForm.aliquota_iva) || 0) / 100).toFixed(2)}</span></div>
                <div className="flex justify-between border-t pt-1 mt-1 font-bold"><span>Totale fattura:</span><span className="text-blue-700">€ {(actionTarget.totale_atteso * (1 + (parseFloat(creaForm.aliquota_iva) || 0) / 100)).toFixed(2)}</span></div>
              </div>
              <div className="flex justify-between gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setCreaMode(false)} disabled={submitting}>← Indietro</Button>
                <Button type="submit" className="bg-blue-600 text-white hover:bg-blue-700" disabled={submitting} data-testid="confirm-crea-fattura">
                  {submitting ? 'Creazione…' : 'Crea Fattura'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
